import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { PageWrapper } from '../components/layout/PageWrapper';
import { useForgeNavigate } from '../components/layout/ForgeTransition';
import { Navbar } from '../components/layout/Navbar';
import { ProgressTracker } from '../components/layout/ProgressTracker';
import { Button } from '../components/shared/Button';
import { useAppState } from '../hooks/useAppState';
import { parseTradovateCSV } from '../utils/csvParser';
import { Upload, CheckCircle, AlertTriangle } from 'lucide-react';
import type { Strategy } from '../types';

export function StatsInput() {
  const forgeNavigate = useForgeNavigate();
  const { updateUser } = useAppState();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [strategy, setStrategy] = useState<string>('');
  const [accountSize, setAccountSize] = useState('');
  const [showCustomSize, setShowCustomSize] = useState(false);
  const [totalTrades, setTotalTrades] = useState('');
  const [winRateInput, setWinRateInput] = useState('');
  const [expectancy, setExpectancy] = useState('');
  const [challengeStatus, setChallengeStatus] = useState('');

  // CSV upload state
  const [csvStatus, setCsvStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [csvMessage, setCsvMessage] = useState('');
  const [csvFileName, setCsvFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const trades = parseInt(totalTrades) || 0;
  const winRate = parseFloat(winRateInput) || 0;
  const exp = parseFloat(expectancy) || 0;

  const isValid =
    strategy !== '' &&
    parseFloat(accountSize) > 0 &&
    trades >= 20 &&
    winRate > 0 &&
    winRate <= 100 &&
    expectancy !== '';

  function processFile(file: File) {
    if (!file.name.endsWith('.csv')) {
      setCsvStatus('error');
      setCsvMessage('Please upload a .csv file.');
      return;
    }

    setCsvFileName(file.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      if (!text) {
        setCsvStatus('error');
        setCsvMessage('Could not read file.');
        return;
      }

      const result = parseTradovateCSV(text);

      if (!result.success) {
        setCsvStatus('error');
        setCsvMessage(result.error);
        return;
      }

      const { stats } = result;

      setTotalTrades(String(stats.totalTrades));

      const wr = stats.totalTrades > 0 ? (stats.winCount / stats.totalTrades) * 100 : 0;
      setWinRateInput(wr.toFixed(1));

      // Calculate expectancy from parsed data
      const isNotionFormat = stats.trades.length > 0 && stats.trades[0].pnl === 0 && stats.trades[0].rFactor !== 0;
      let avgW: number;
      let avgL: number;

      if (isNotionFormat) {
        avgW = stats.avgWinnerDollar;
        avgL = stats.avgLoserDollar;
      } else if (stats.avgLoserDollar > 0) {
        avgW = stats.avgWinnerDollar / stats.avgLoserDollar;
        avgL = 1.0;
      } else {
        avgW = stats.avgWinnerDollar;
        avgL = 0;
      }

      const wrDecimal = wr / 100;
      const exp = (wrDecimal * avgW) - ((1 - wrDecimal) * avgL);
      setExpectancy(exp.toFixed(2));

      setCsvStatus('success');
      setCsvMessage(
        `Parsed ${stats.totalTrades} trades — ${stats.winCount} wins, ${stats.totalTrades - stats.winCount} losses. Win rate: ${wr.toFixed(1)}%, expectancy: ${exp.toFixed(2)}R.`
      );
    };

    reader.onerror = () => {
      setCsvStatus('error');
      setCsvMessage('Failed to read file.');
    };

    reader.readAsText(file);
  }

  function handleCSVUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    processFile(file);
    e.target.value = '';
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
  }

  function handleSubmit() {
    if (!isValid || !strategy) return;

    const wr = winRate / 100; // convert % to decimal

    // Derive avgWinner/avgLoser from expectancy + winRate
    // Assume avgLoser = 1.0R, then: expectancy = wr * avgW - (1-wr) * 1.0
    // So avgW = (expectancy + (1-wr)) / wr
    const avgLoser = 1.0;
    const avgWinner = wr > 0 ? (exp + (1 - wr)) / wr : 0;

    updateUser({
      currentStrategy: strategy as Strategy,
      accountSize: parseFloat(accountSize),
      stats: {
        totalTrades: trades,
        winCount: Math.round(wr * trades),
        avgWinner,
        avgLoser,
        accountDrawdown: 0,
        winRate: wr,
        expectancy: exp,
      },
      monthsTrading: 0,
      challengeStatus: (challengeStatus as 'Eval' | 'Funded' | 'Live') || null,
    });

    forgeNavigate('/firm', 4);
  }

  // ─── Form field definitions ───────────────────────────────────────────────

  const PRESET_SIZES = [25000, 50000, 100000, 150000];

  function handleSizePreset(size: number) {
    setAccountSize(String(size));
    setShowCustomSize(false);
  }

  function handleOtherSize() {
    setShowCustomSize(true);
    setAccountSize('');
  }

  const fields: {
    label: string;
    type?: string;
    placeholder?: string;
    suffix?: string;
    value: string;
    onChange: (v: string) => void;
    helpText?: string;
    error?: string;
    step?: string;
    isSelect?: boolean;
    options?: { value: string; label: string }[];
  }[] = [
    {
      label: 'Total Trades',
      type: 'number',
      placeholder: '87',
      value: totalTrades,
      onChange: (v) => setTotalTrades(v),
      helpText: 'Min 20',
      error: totalTrades && trades < 20 ? 'Minimum 20 trades' : undefined,
    },
    {
      label: 'Win Rate',
      type: 'number',
      step: '0.1',
      placeholder: '72.5',
      suffix: '%',
      value: winRateInput,
      onChange: (v) => setWinRateInput(v),
      error: winRateInput && winRate > 100 ? 'Cannot exceed 100%' : undefined,
    },
    {
      label: 'Expectancy',
      type: 'number',
      step: '0.01',
      placeholder: '0.35',
      suffix: 'R',
      value: expectancy,
      onChange: (v) => setExpectancy(v),
      helpText: 'Average R per trade',
    },
    {
      label: 'Challenge Status',
      isSelect: true,
      options: [
        { value: 'Eval', label: 'Eval' },
        { value: 'Funded', label: 'Funded' },
        { value: 'Live', label: 'Live' },
      ],
      value: challengeStatus,
      onChange: (v: string) => setChallengeStatus(v),
    },
  ];

  return (
    <PageWrapper warmth={0.3}>
      <Navbar />
      <main className="min-h-screen pt-16">
        <ProgressTracker currentStep={4} />

        <div className="max-w-[480px] mx-auto px-8 pb-16">
          <h1 className="text-xl md:text-2xl font-bold font-mono tracking-wide text-white/90 text-center mb-2">
            Enter Your Stats
          </h1>
          <p className="text-[12px] font-mono text-white/30 text-center mb-8 tracking-wide">
            We need your track record to build your protocol.
          </p>

          {/* Single glass card */}
          <div className="bg-white/[0.02] border border-white/[0.05] rounded-xl p-6 backdrop-blur-md space-y-4">
            {/* CSV upload row */}
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.04, duration: 0.3 }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleCSVUpload}
                className="hidden"
              />
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className={`w-full flex items-center gap-3 py-2.5 px-3 rounded-lg border border-dashed transition-all duration-300 cursor-pointer group ${
                    isDragging
                      ? 'border-[rgba(60,160,220,0.4)] bg-[rgba(60,160,220,0.06)]'
                      : 'border-white/[0.08] hover:border-white/[0.15] bg-white/[0.02] hover:bg-white/[0.04]'
                  }`}
                >
                  <Upload className={`w-4 h-4 transition-colors flex-shrink-0 ${isDragging ? 'text-[rgba(60,160,220,0.6)]' : 'text-white/25 group-hover:text-white/45'}`} />
                  <div className="text-left">
                    <span className={`text-[12px] font-mono transition-colors ${isDragging ? 'text-[rgba(60,160,220,0.8)]' : 'text-white/45 group-hover:text-white/65'}`}>
                      {isDragging ? 'Drop CSV here' : 'Upload CSV to auto-fill'}
                    </span>
                  </div>
                </button>

                {csvStatus !== 'idle' && (
                  <div
                    className={`mt-2 flex items-start gap-2 px-3 py-2 rounded-lg text-[11px] font-mono leading-relaxed ${
                      csvStatus === 'success'
                        ? 'bg-[#3DBF7A]/10 border border-[#3DBF7A]/20 text-[#3DBF7A]'
                        : 'bg-[#E84040]/10 border border-[#E84040]/20 text-[#E84040]'
                    }`}
                    style={{ animation: 'card-enter 0.3s cubic-bezier(0.16,1,0.3,1) both' }}
                  >
                    {csvStatus === 'success'
                      ? <CheckCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      : <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    }
                    <div>
                      {csvFileName && <span className="text-white/50 block mb-0.5">{csvFileName}</span>}
                      {csvMessage}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Divider */}
            <motion.div
              className="h-px bg-white/[0.04]"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.08, duration: 0.4 }}
              style={{ transformOrigin: 'left' }}
            />

            {/* Strategy */}
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.06 }}
            >
              <label className="text-[10px] font-mono text-white/35 uppercase tracking-wider block mb-1.5">
                Strategy
              </label>
              <div className="relative">
                <select
                  value={strategy}
                  onChange={(e) => setStrategy(e.target.value)}
                  className="w-full h-10 bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 text-sm font-mono text-white/80 appearance-none cursor-pointer focus:outline-none focus:border-white/[0.15] transition-colors"
                >
                  <option value="" className="bg-[#0F172A]">Select...</option>
                  <option value="TCL" className="bg-[#0F172A]">TCL — Trend Continuation Ladder</option>
                  <option value="SMOG" className="bg-[#0F172A]">SMOG — Smart Money Only Gains</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-white/25 text-xs">▼</div>
              </div>
            </motion.div>

            {/* Account Size Picker */}
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.11 }}
            >
              <label className="text-[10px] font-mono text-white/35 uppercase tracking-wider block mb-2">
                Account Size
              </label>
              <div className="flex gap-2">
                {PRESET_SIZES.map((size) => {
                  const isActive = accountSize === String(size) && !showCustomSize;
                  return (
                    <button
                      key={size}
                      onClick={() => handleSizePreset(size)}
                      className={`flex-1 py-2 rounded-lg text-[12px] font-mono font-medium transition-all duration-200 cursor-pointer ${
                        isActive
                          ? 'bg-[rgba(60,200,210,0.15)] text-[rgba(100,230,230,0.95)] border border-[rgba(60,200,210,0.4)] shadow-[0_0_10px_rgba(60,200,210,0.12)]'
                          : 'bg-white/[0.03] text-white/45 border border-white/[0.06] hover:bg-white/[0.06] hover:text-white/65'
                      }`}
                    >
                      ${size >= 1000 ? `${size / 1000}K` : size}
                    </button>
                  );
                })}
                <button
                  onClick={handleOtherSize}
                  className={`px-3 py-2 rounded-lg text-[12px] font-mono font-medium transition-all duration-200 cursor-pointer ${
                    showCustomSize
                      ? 'bg-[rgba(60,200,210,0.15)] text-[rgba(100,230,230,0.95)] border border-[rgba(60,200,210,0.4)] shadow-[0_0_10px_rgba(60,200,210,0.12)]'
                      : 'bg-white/[0.03] text-white/45 border border-white/[0.06] hover:bg-white/[0.06] hover:text-white/65'
                  }`}
                >
                  Other
                </button>
              </div>
              {showCustomSize && (
                <motion.div
                  className="relative mt-2"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ duration: 0.2 }}
                >
                  <input
                    type="number"
                    placeholder="Enter amount"
                    value={accountSize}
                    onChange={(e) => setAccountSize(e.target.value)}
                    autoFocus
                    className="w-full h-10 bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 text-sm font-mono text-white/80 placeholder:text-white/15 focus:outline-none focus:border-white/[0.15] transition-colors"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-mono text-white/25">$</span>
                </motion.div>
              )}
            </motion.div>

            {/* Remaining fields */}
            {fields.map((field, i) => (
              <motion.div
                key={field.label}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.16 + i * 0.05 }}
              >
                <label className="text-[10px] font-mono text-white/35 uppercase tracking-wider block mb-1.5">
                  {field.label}
                </label>

                {field.isSelect ? (
                  <div className="relative">
                    <select
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="w-full h-10 bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 text-sm font-mono text-white/80 appearance-none cursor-pointer focus:outline-none focus:border-white/[0.15] transition-colors"
                    >
                      <option value="" className="bg-[#0F172A]">Select...</option>
                      {field.options?.map((opt) => (
                        <option key={opt.value} value={opt.value} className="bg-[#0F172A]">
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-white/25 text-xs">▼</div>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type={field.type || 'text'}
                      step={field.step}
                      placeholder={field.placeholder}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="w-full h-10 bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 text-sm font-mono text-white/80 placeholder:text-white/15 focus:outline-none focus:border-white/[0.15] transition-colors"
                    />
                    {field.suffix && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-mono text-white/25">{field.suffix}</span>
                    )}
                  </div>
                )}

                {field.error && (
                  <p className="text-[10px] font-mono text-[#E84040] mt-1">{field.error}</p>
                )}
                {field.helpText && !field.error && (
                  <p className="text-[10px] font-mono text-white/20 mt-1">{field.helpText}</p>
                )}
              </motion.div>
            ))}
          </div>

          {/* Submit button */}
          <motion.div
            className="mt-8 flex justify-center"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.06 + fields.length * 0.05 + 0.1, duration: 0.35 }}
          >
            <Button onClick={handleSubmit} disabled={!isValid}>
              Select Firm
            </Button>
          </motion.div>
        </div>
      </main>
    </PageWrapper>
  );
}
